<template>
  <loading-wrapper :is-loading="!workspace || isLoadingWorkspace">
    <div v-if="workspace" class="workspace-details">
      <div class="title-panel">
        <h1>{{ workspace.name }}</h1>
      </div>
    </div>
    <PVTabs
      v-if="store.state.userRoles?.length && workspace"
      v-model:value="activeTab"
      lazy
    >
      <PVTabList>
        <router-link
          v-if="canAccessWorkspaces"
          :to="getTabLink(WorkspaceDetailsTab.SETTINGS)"
        >
          <PVTab :value="WorkspaceDetailsTab.SETTINGS">
            {{ t('workspaceDetailsTabs.settings') }}
          </PVTab>
        </router-link>
        <router-link
          v-if="canManageWorkspaces"
          :to="getTabLink(WorkspaceDetailsTab.GROUPS)"
        >
          <PVTab :value="WorkspaceDetailsTab.GROUPS">
            {{ t('workspaceDetailsTabs.groups') }}
          </PVTab>
        </router-link>
        <router-link :to="getTabLink(WorkspaceDetailsTab.DOCUMENTS)">
          <PVTab :value="WorkspaceDetailsTab.DOCUMENTS">
            {{ t('workspaceDetailsTabs.documents') }}
          </PVTab>
        </router-link>
      </PVTabList>
      <PVTabPanels>
        <PVTabPanel :value="WorkspaceDetailsTab.SETTINGS">
          <workspace-settings :workspace="workspace" @update="fetchWorkspace" />
        </PVTabPanel>
        <PVTabPanel
          v-if="canManageWorkspaces"
          :value="WorkspaceDetailsTab.GROUPS"
        >
          <workspace-groups :workspace="workspace" />
        </PVTabPanel>
        <PVTabPanel :value="WorkspaceDetailsTab.DOCUMENTS">
          <workspace-documents
            :workspace-id="workspace._id"
            :workspace-location="workspace.location"
          />
        </PVTabPanel>
      </PVTabPanels>
    </PVTabs>
  </loading-wrapper>
</template>

<script lang="ts" setup>
  import { useGetWorkspace } from '@/composables/api/workspaces/useGetWorkspace';
  import { useRoles } from '@/composables/useRoles';
  import { useTabs } from '@/composables/useTabs';
  import { Route, routeParams } from '@/routes';
  import { WorkspaceDetailsTab } from './WorkspaceDetails.types';
  import { RouteLocationRaw } from 'vue-router';
  import { useStore } from 'vuex';
  import { useI18n } from 'vue-i18n';

  const props = defineProps<{
    workspaceId: string;
  }>();

  const store = useStore();
  const { t } = useI18n();
  const { canAccessWorkspaces, canManageWorkspaces } = useRoles();

  const { workspace, fetchWorkspace, isLoadingWorkspace } = useGetWorkspace(
    props.workspaceId,
  );

  const { activeTab } = useTabs(
    Route.WORKSPACE_DETAILS,
    WorkspaceDetailsTab,
    WorkspaceDetailsTab.SETTINGS,
  );
  function getTabLink(tab: WorkspaceDetailsTab): RouteLocationRaw {
    return {
      name: Route.WORKSPACE_DETAILS,
      params: {
        [routeParams[Route.WORKSPACE_DETAILS].workspaceId]: props.workspaceId,
        [routeParams[Route.WORKSPACE_DETAILS].tab]: tab,
      },
    };
  }
</script>

<style scoped lang="scss">
  .title-panel {
    margin-bottom: 4.25rem;
    display: flex;
    align-items: center;
    justify-content: space-between;

    h1 {
      font-weight: 700;
      font-size: 50px;
      line-height: 36px;
    }
  }
</style>
